import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Plus, Trash2, ChevronRight, ChevronLeft, AlertTriangle, RotateCcw, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

// ─── SVG GLASS COMPONENTS ────────────────────────────────────────────────────

function CopaGlass({ step }: { step: number }) {
  return (
    <svg viewBox="0 0 140 215" className="h-52 w-auto mx-auto">
      <defs><clipPath id="cg"><path d="M26,10 Q2,68 6,108 Q8,155 70,162 Q132,155 134,108 Q138,68 114,10Z"/></clipPath></defs>
      {step >= 1 && <g clipPath="url(#cg)">
        <rect x="6" y="108" width="128" height="54" fill="rgba(190,225,255,0.5)"/>
        {[[22,122],[52,116],[84,122],[112,118]].map(([x,y],i) => <rect key={i} x={x} y={y} width="16" height="13" rx="3" fill="rgba(255,255,255,0.85)" stroke="rgba(130,190,255,0.7)" strokeWidth="1"/>)}
      </g>}
      {step >= 2 && <g clipPath="url(#cg)"><rect x="6" y="86" width="128" height="25" fill="rgba(245,215,90,0.55)"/></g>}
      {step >= 3 && <g clipPath="url(#cg)">
        <rect x="6" y="46" width="128" height="44" fill="rgba(185,238,252,0.45)"/>
        {[[30,58],[70,54],[108,62]].map(([cx,cy],i) => <circle key={i} cx={cx} cy={cy} r="3" fill="rgba(255,255,255,0.55)"/>)}
      </g>}
      {step >= 4 && <text x="116" y="36" fontSize="22" textAnchor="middle">🍋</text>}
      <path d="M26,10 Q2,68 6,108 Q8,155 70,162 Q132,155 134,108 Q138,68 114,10Z" fill="none" stroke="currentColor" strokeWidth="2.5" opacity="0.65"/>
      <path d="M26,10 Q70,4 114,10" fill="none" stroke="currentColor" strokeWidth="2" opacity="0.55"/>
      <line x1="70" y1="162" x2="70" y2="198" stroke="currentColor" strokeWidth="3" opacity="0.65"/>
      <ellipse cx="70" cy="199" rx="33" ry="7" fill="none" stroke="currentColor" strokeWidth="2" opacity="0.55"/>
    </svg>
  );
}

function HighballGlass({ step }: { step: number }) {
  return (
    <svg viewBox="0 0 88 205" className="h-52 w-auto mx-auto">
      <defs><clipPath id="hg"><path d="M10,10 L15,190 L73,190 L78,10Z"/></clipPath></defs>
      {step >= 1 && <g clipPath="url(#hg)">
        <rect x="15" y="100" width="58" height="90" fill="rgba(190,225,255,0.5)"/>
        {[[19,142],[40,136],[59,144]].map(([x,y],i) => <rect key={i} x={x} y={y} width="14" height="12" rx="2" fill="rgba(255,255,255,0.85)" stroke="rgba(130,190,255,0.7)" strokeWidth="1"/>)}
      </g>}
      {step >= 2 && <g clipPath="url(#hg)"><rect x="15" y="78" width="58" height="25" fill="rgba(245,215,90,0.55)"/></g>}
      {step >= 3 && <g clipPath="url(#hg)">
        <rect x="15" y="14" width="58" height="67" fill="rgba(185,238,252,0.45)"/>
        {[[26,28],[50,36],[68,24]].map(([cx,cy],i) => <circle key={i} cx={cx} cy={cy} r="2.5" fill="rgba(255,255,255,0.55)"/>)}
      </g>}
      {step >= 4 && <text x="68" y="22" fontSize="18">🍋</text>}
      <path d="M10,10 L15,190 L73,190 L78,10Z" fill="none" stroke="currentColor" strokeWidth="2.5" opacity="0.65"/>
      <line x1="15" y1="190" x2="73" y2="190" stroke="currentColor" strokeWidth="3" opacity="0.65"/>
      <path d="M10,10 Q44,5 78,10" fill="none" stroke="currentColor" strokeWidth="2" opacity="0.55"/>
    </svg>
  );
}

function TumblerGlass({ withIce }: { withIce: boolean }) {
  const wy = withIce ? 65 : 78; const wh = withIce ? 22 : 40;
  return (
    <svg viewBox="0 0 115 132" className="h-36 w-auto mx-auto">
      <defs><clipPath id="tg"><path d="M8,10 L18,120 L97,120 L107,10Z"/></clipPath></defs>
      {withIce && <g clipPath="url(#tg)">
        <rect x="18" y="82" width="79" height="38" fill="rgba(190,225,255,0.5)"/>
        {[[24,90],[54,86],[78,92]].map(([x,y],i) => <rect key={i} x={x} y={y} width="16" height="14" rx="3" fill="rgba(255,255,255,0.85)" stroke="rgba(130,190,255,0.7)" strokeWidth="1"/>)}
      </g>}
      <g clipPath="url(#tg)"><rect x="18" y={wy} width="79" height={wh} fill="rgba(185,110,25,0.42)"/></g>
      <path d="M8,10 L18,120 L97,120 L107,10Z" fill="none" stroke="currentColor" strokeWidth="2.5" opacity="0.65"/>
      <line x1="18" y1="120" x2="97" y2="120" stroke="currentColor" strokeWidth="3" opacity="0.65"/>
      <path d="M8,10 Q57,5 107,10" fill="none" stroke="currentColor" strokeWidth="2" opacity="0.55"/>
    </svg>
  );
}

function BrandyBalloon({ tilted }: { tilted: boolean }) {
  return (
    <div className={cn('transition-all duration-700 ease-in-out', tilted ? '[transform:rotate(88deg)_translateX(30px)]' : '')} style={{ transformOrigin: '77px 195px' }}>
      <svg viewBox="0 0 155 218" className="h-52 w-auto mx-auto">
        <defs><clipPath id="bg2"><ellipse cx="77" cy="110" rx="63" ry="80"/></clipPath></defs>
        <g clipPath="url(#bg2)"><rect x="14" y="152" width="126" height="42" fill="rgba(185,110,25,0.42)"/></g>
        <ellipse cx="77" cy="110" rx="63" ry="80" fill="none" stroke="currentColor" strokeWidth="2.5" opacity="0.65"/>
        <path d="M67,32 L63,18 L91,18 L87,32" fill="none" stroke="currentColor" strokeWidth="2.5" opacity="0.65"/>
        <path d="M63,18 Q77,13 91,18" fill="none" stroke="currentColor" strokeWidth="2" opacity="0.55"/>
        <line x1="77" y1="190" x2="77" y2="208" stroke="currentColor" strokeWidth="3" opacity="0.65"/>
        <ellipse cx="77" cy="209" rx="36" ry="7" fill="none" stroke="currentColor" strokeWidth="2" opacity="0.55"/>
      </svg>
    </div>
  );
}

function WineGlassSVG({ fillFrac, uid }: { fillFrac: number; uid: string }) {
  const liqY = 124 - fillFrac * 110;
  return (
    <svg viewBox="0 0 90 195" className="h-44 w-auto mx-auto">
      <defs><clipPath id={`wg-${uid}`}><path d="M11,14 Q3,58 4,82 Q4,124 45,130 Q86,124 86,82 Q87,58 79,14Z"/></clipPath></defs>
      <g clipPath={`url(#wg-${uid})`}><rect x="4" y={liqY} width="82" height={135 - liqY} fill="hsl(var(--primary) / 0.28)"/></g>
      <line x1="7" y1={liqY} x2="83" y2={liqY} stroke="hsl(var(--primary))" strokeWidth="1.5" strokeDasharray="4,3" opacity="0.7"/>
      <path d="M11,14 Q3,58 4,82 Q4,124 45,130 Q86,124 86,82 Q87,58 79,14Z" fill="none" stroke="currentColor" strokeWidth="2" opacity="0.65"/>
      <path d="M11,14 Q45,8 79,14" fill="none" stroke="currentColor" strokeWidth="1.5" opacity="0.55"/>
      <line x1="45" y1="130" x2="45" y2="168" stroke="currentColor" strokeWidth="2.5" opacity="0.65"/>
      <ellipse cx="45" cy="169" rx="27" ry="6" fill="none" stroke="currentColor" strokeWidth="2" opacity="0.55"/>
    </svg>
  );
}

// ─── POUR GUIDE ──────────────────────────────────────────────────────────────

const GLASSES = [
  {
    id: 'copa', emoji: '🎈', name: 'Copa / Bowl Glass',
    uses: 'Gin & Tonic · Aperol Spritz · Premium cocktails',
    steps: [
      { label: 'Start with a clean Copa glass', icon: '✨', detail: 'Polished and spot-free. Chill it if possible.', isGarnish: false },
      { label: 'Fill to ½ with ice', icon: '🧊', detail: 'The wide bowl needs lots of ice to keep the drink cold. Go roughly halfway up.', isGarnish: false },
      { label: 'Pour the spirit over the ice', icon: '🫗', detail: 'Single = 25ml, Double = 50ml. Use a jigger — always measure accurately.', isGarnish: false },
      { label: 'Add exactly HALF the mixer', icon: '💧', detail: 'Pour gently over a bar spoon. Only half the bottle/can — preserves carbonation and allows the guest to top up.', isGarnish: false },
      { label: '🚨 ALWAYS ASK before adding garnish!', icon: '🍋', detail: '"Would you like a slice of lime and cucumber?" — some guests have allergies or simply don\'t want it. Never assume.', isGarnish: true },
    ],
  },
  {
    id: 'highball', emoji: '🥤', name: 'Highball / Tall Glass',
    uses: 'Rum & Coke · Vodka mixer · Whisky & Ginger · JD & Coke',
    steps: [
      { label: 'Start with a clean tall glass', icon: '✨', detail: 'Long glass, polished, upright.', isGarnish: false },
      { label: 'Fill to ½ with ice', icon: '🧊', detail: 'Tall glass needs ice to the halfway mark to chill everything effectively.', isGarnish: false },
      { label: 'Pour the spirit over the ice', icon: '🫗', detail: 'Measure carefully — 25ml single or 50ml double as requested.', isGarnish: false },
      { label: 'Top with the full mixer', icon: '💧', detail: 'In a tall glass you use the full can/bottle of mixer. Pour slowly over a bar spoon to keep it fizzy.', isGarnish: false },
      { label: '🚨 ALWAYS ASK before adding garnish!', icon: '🍋', detail: '"Would you like a wedge of lime/lemon with that?" — never add without checking first.', isGarnish: true },
    ],
  },
  {
    id: 'tumbler', emoji: '🥃', name: 'Tumbler / Rocks Glass',
    uses: 'Whisky · Cognac · Negroni · Old Fashioned',
    steps: [
      { label: 'Start with a clean tumbler', icon: '✨', detail: 'Short, wide glass — ensure it is spotlessly clean.', isGarnish: false },
      { label: 'Ice — ALWAYS ASK the guest', icon: '🧊', detail: '"Would you like ice with that?" — many whisky drinkers prefer it neat. Never add ice without asking!', isGarnish: true },
      { label: 'Pour the spirit', icon: '🫗', detail: '25ml standard — or as specified. Whisky drinkers often know what they want so follow their lead.', isGarnish: false },
    ],
  },
  {
    id: 'brandy', emoji: '🍷', name: 'Brandy Balloon / Snifter',
    uses: 'Brandy · Cognac · Armagnac · Some liqueurs',
    steps: [
      { label: 'Use the balloon/snifter glass', icon: '✨', detail: 'The bulbous shape concentrates the aroma. Present it to the guest and explain this!', isGarnish: false },
      { label: 'Pour the spirit — no ice needed', icon: '🫗', detail: '25ml or 50ml as requested. The warmth of the hand held under the balloon slowly warms the spirit — tip the glass to show how liquid stays in the bulb!', isGarnish: false },
    ],
    specialNote: 'Tilt the glass on its side — the liquid stays in the bulb! This is by design — the round bottom holds the spirit, concentrating the aroma toward the nose.',
  },
];

function PourGuideSection() {
  const [glassIdx, setGlassIdx] = useState(0);
  const [step, setStep] = useState(0);
  const [iceToggle, setIceToggle] = useState(true);
  const [tilted, setTilted] = useState(false);
  const g = GLASSES[glassIdx];

  const selectGlass = (i: number) => { setGlassIdx(i); setStep(0); setTilted(false); };
  const maxStep = g.steps.length - 1;
  const currentStep = g.steps[step];

  return (
    <div className="space-y-4">
      {/* Glass selector */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {GLASSES.map((gl, i) => (
          <button key={gl.id} onClick={() => selectGlass(i)}
            className={cn('border rounded-xl p-2.5 text-center transition-all text-sm',
              i === glassIdx ? 'border-primary bg-primary/10 font-semibold text-primary' : 'border-border hover:border-primary/40 hover:bg-muted/50 text-muted-foreground')}>
            <div className="text-2xl mb-1">{gl.emoji}</div>
            <div className="text-xs leading-tight">{gl.name.split(' / ')[0]}</div>
          </button>
        ))}
      </div>

      {/* Glass info */}
      <div className="border border-border rounded-xl bg-card overflow-hidden">
        <div className="bg-muted/40 border-b border-border px-4 py-2.5 flex items-center gap-2">
          <span className="text-lg">{g.emoji}</span>
          <div>
            <p className="text-sm font-bold text-foreground">{g.name}</p>
            <p className="text-xs text-muted-foreground">{g.uses}</p>
          </div>
        </div>

        <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-5 items-center">
          {/* Glass illustration */}
          <div className="flex flex-col items-center gap-3 py-2">
            {g.id === 'copa' && <CopaGlass step={step} />}
            {g.id === 'highball' && <HighballGlass step={step} />}
            {g.id === 'tumbler' && <TumblerGlass withIce={iceToggle && step >= 1} />}
            {g.id === 'brandy' && <BrandyBalloon tilted={tilted} />}
            {g.id === 'brandy' && (
              <Button size="sm" variant="outline" className="gap-1.5 text-xs" onClick={() => setTilted(v => !v)}>
                {tilted ? <RotateCcw className="w-3 h-3" /> : '↻'} {tilted ? 'Upright' : 'Tilt the glass!'}
              </Button>
            )}
          </div>

          {/* Steps */}
          <div className="space-y-3">
            {/* Step dots */}
            <div className="flex items-center gap-1.5 mb-1">
              {g.steps.map((_, i) => (
                <button key={i} onClick={() => setStep(i)}
                  className={cn('w-2.5 h-2.5 rounded-full transition-all border', i === step ? 'bg-primary border-primary scale-125' : i < step ? 'bg-primary/40 border-primary/40' : 'bg-muted border-border')}>
                </button>
              ))}
              <span className="text-xs text-muted-foreground ml-1">Step {step + 1}/{g.steps.length}</span>
            </div>

            {/* Current step card */}
            <div className={cn('rounded-xl border p-4 transition-all', currentStep.isGarnish ? 'border-destructive/50 bg-destructive/5' : 'border-border bg-muted/20')}>
              {currentStep.isGarnish && (
                <div className="flex items-center gap-1.5 text-destructive font-bold text-xs mb-2 uppercase tracking-wider">
                  <AlertTriangle className="w-3.5 h-3.5" /> Always Ask First!
                </div>
              )}
              <p className="text-base font-semibold text-foreground mb-1.5">{currentStep.icon} {currentStep.label.replace('🚨 ', '')}</p>
              <p className="text-sm text-muted-foreground leading-relaxed">{currentStep.detail}</p>
              {g.id === 'tumbler' && step === 1 && (
                <button onClick={() => setIceToggle(v => !v)}
                  className={cn('mt-2.5 text-xs px-3 py-1 rounded-full border font-medium transition-all', iceToggle ? 'bg-primary/10 border-primary text-primary' : 'border-border text-muted-foreground')}>
                  {iceToggle ? '🧊 Ice selected' : '🚫 No ice selected'}
                </button>
              )}
            </div>

            {g.specialNote && step === maxStep && (
              <div className="bg-primary/5 border border-primary/20 rounded-lg px-3 py-2.5 text-xs text-muted-foreground leading-relaxed">
                <span className="font-semibold text-primary">💡 Did you know? </span>{g.specialNote}
              </div>
            )}

            {/* Navigation */}
            <div className="flex items-center gap-2 pt-1">
              <Button variant="outline" size="sm" className="gap-1" onClick={() => setStep(s => Math.max(0, s - 1))} disabled={step === 0}>
                <ChevronLeft className="w-3.5 h-3.5" /> Back
              </Button>
              <Button size="sm" className="gap-1 flex-1" onClick={() => setStep(s => Math.min(maxStep, s + 1))} disabled={step === maxStep}>
                {step === maxStep ? '✓ Done' : <>Next <ChevronRight className="w-3.5 h-3.5" /></>}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Garnish rule */}
      <div className="flex items-start gap-3 bg-destructive/5 border border-destructive/30 rounded-xl px-4 py-3">
        <AlertTriangle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-bold text-destructive">HOUSE RULE: Always ask before adding garnish</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Never assume a garnish is wanted. Always ask: <em>"Would you like [lemon/lime/cucumber] with that?"</em> — guests may have allergies, preferences, or simply not want it. This applies to every drink, every time.
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── WINE GUIDE ───────────────────────────────────────────────────────────────

function WineGuideSection() {
  const measures = [
    { uid: 'sm', ml: '125ml', fill: 0.25, label: 'Taster', sub: 'Strictly on request only\n"A smaller measure"', badge: 'On Request' },
    { uid: 'md', ml: '175ml', fill: 0.40, label: 'Standard', sub: 'Default pour — refer to as\n"Standard glass"', badge: 'Default', isStandard: true },
    { uid: 'lg', ml: '250ml', fill: 0.62, label: 'Large', sub: 'Third of a bottle — refer to as\n"Large glass"', badge: 'Large' },
  ];
  return (
    <div className="space-y-5">
      <div className="bg-primary/5 border border-primary/20 rounded-xl px-4 py-3">
        <p className="text-sm font-semibold text-foreground mb-1">🍷 Wine Measure Language</p>
        <p className="text-xs text-muted-foreground leading-relaxed">
          We <strong>never say "small"</strong> for wine. Use the correct terms: <strong>Taster</strong> (125ml, on demand only), <strong>Standard</strong> (175ml, default), and <strong>Large</strong> (250ml). Always confirm which size the guest wants when taking an order.
        </p>
      </div>

      {/* Three glass comparison */}
      <div className="grid grid-cols-3 gap-3">
        {measures.map(m => (
          <div key={m.uid} className={cn('border rounded-xl p-3 flex flex-col items-center gap-1 text-center', m.isStandard ? 'border-primary/40 bg-primary/5' : 'border-border bg-card')}>
            <WineGlassSVG fillFrac={m.fill} uid={m.uid} />
            <span className={cn('text-lg font-bold', m.isStandard ? 'text-primary' : 'text-foreground')}>{m.ml}</span>
            <Badge variant={m.isStandard ? 'default' : 'outline'} className="text-xs">{m.badge}</Badge>
            <p className="text-xs text-foreground font-semibold mt-0.5">{m.label}</p>
            {m.sub.split('\n').map((line, i) => <p key={i} className="text-xs text-muted-foreground">{line}</p>)}
          </div>
        ))}
      </div>

      {/* Rules */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {[
          { icon: '🗣️', title: 'Taking a wine order', text: '"Would you like a standard (175ml) or a large (250ml)?" — always offer both sizes.' },
          { icon: '📏', title: '125ml — Taster measure', text: 'Only pour a 125ml if a guest specifically asks for a smaller measure. Do not proactively offer this size.' },
          { icon: '🍾', title: 'Recommending wine', text: '"We have a lovely Sauvignon Blanc — crisp and dry, very popular. Or our Malbec is rich and full-bodied. Which style do you prefer?"' },
          { icon: '✅', title: 'Upselling wine', text: 'Always ask: "Would you like a large glass?" when a guest orders without specifying. A large glass is nearly a third of a bottle and great value.' },
        ].map((tip, i) => (
          <div key={i} className="border border-border rounded-xl bg-card p-3 flex items-start gap-3">
            <span className="text-xl shrink-0">{tip.icon}</span>
            <div><p className="text-xs font-semibold text-foreground mb-0.5">{tip.title}</p><p className="text-xs text-muted-foreground leading-relaxed italic">{tip.text}</p></div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── PRODUCT KNOWLEDGE ───────────────────────────────────────────────────────

const STARTER_ITEMS: Record<string, Array<{ q: string; a: string }>> = {
  'Spirits & Serves': [
    { q: 'What is the house gin?', a: '[Add your house gin here] — served in a Copa glass over ice with [tonic], garnished with [ask first].' },
    { q: 'What is the house vodka?', a: '[Add your house vodka here] — served in a highball over ice with choice of mixer.' },
    { q: 'What rum do we stock?', a: '[Add your rums here] — standard serve is over ice in a highball with [mixer].' },
    { q: 'Correct measure for a single spirit?', a: '25ml. Always use a jigger. A double is 50ml. Never free-pour.' },
  ],
  'Wine': [
    { q: 'What are our house wines?', a: '[Add your house wines here] — Red: [name], White: [name], Rosé: [name].' },
    { q: 'Wine measures and what to call them?', a: '125ml = Taster (on request only), 175ml = Standard (default), 250ml = Large. Never say "small".' },
    { q: 'Do we serve wine by the bottle?', a: '[Add your bottle wine range here]. Always suggest a bottle when a table orders two large glasses.' },
  ],
  'Beer & Draught': [
    { q: 'What lagers do we have on draught?', a: '[Add your draught lagers here] — served in a branded pint or half-pint glass.' },
    { q: 'What ales do we stock?', a: '[Add your ales here] — cask or keg. Know your current guest ale.' },
    { q: 'Do we serve stout?', a: '[Add details here] — if yes, always describe the pour process to guests as needed.' },
  ],
  'Cocktails & Specials': [
    { q: 'What is our signature cocktail?', a: '[Add your signature cocktail name and brief recipe here].' },
    { q: 'What classic cocktails can we make?', a: '[List the cocktails your bar can confidently make — e.g. Old Fashioned, Negroni, Mojito].' },
    { q: 'Aperol Spritz recipe', a: '3 parts prosecco · 2 parts Aperol · 1 splash soda · orange slice garnish (ask first!) · served in Copa glass over ice.' },
  ],
  'Non-Alcoholic': [
    { q: 'What premium soft drinks do we stock?', a: '[Add your premium soft drinks here — e.g. Fever-Tree mixers, Belvoir pressés, etc.].' },
    { q: 'Do we have non-alcoholic spirit options?', a: '[Add your zero-alcohol spirit alternatives here — e.g. Seedlip, Lucky Saint, etc.].' },
  ],
};

const STORAGE_KEY = 'rb-product-knowledge-v1';

function ProductKnowledgeSection() {
  const [items, setItems] = useState<Record<string, Array<{ q: string; a: string }>>>(() => {
    try { const s = localStorage.getItem(STORAGE_KEY); return s ? JSON.parse(s) : STARTER_ITEMS; } catch { return STARTER_ITEMS; }
  });
  const [cat, setCat] = useState(Object.keys(STARTER_ITEMS)[0]);
  const [flipped, setFlipped] = useState<Record<string, boolean>>({});
  const [adding, setAdding] = useState(false);
  const [newQ, setNewQ] = useState('');
  const [newA, setNewA] = useState('');
  const [editIdx, setEditIdx] = useState<number | null>(null);

  const save = (updated: typeof items) => { setItems(updated); try { localStorage.setItem(STORAGE_KEY, JSON.stringify(updated)); } catch {} };

  const addItem = () => {
    if (!newQ.trim() || !newA.trim()) { toast.error('Please enter both question and answer'); return; }
    const updated = { ...items, [cat]: [...(items[cat] || []), { q: newQ.trim(), a: newA.trim() }] };
    save(updated); setNewQ(''); setNewA(''); setAdding(false);
    toast.success('Item added!');
  };

  const deleteItem = (i: number) => {
    const updated = { ...items, [cat]: items[cat].filter((_, idx) => idx !== i) };
    save(updated);
  };

  const updateItem = (i: number, q: string, a: string) => {
    const updated = { ...items, [cat]: items[cat].map((item, idx) => idx === i ? { q, a } : item) };
    save(updated); setEditIdx(null);
  };

  const catItems = items[cat] || [];
  const allCats = Object.keys(STARTER_ITEMS);

  return (
    <div className="space-y-4">
      <div className="bg-primary/5 border border-primary/20 rounded-xl px-4 py-3 flex items-start gap-3">
        <Sparkles className="w-4 h-4 text-primary shrink-0 mt-0.5" />
        <p className="text-xs text-muted-foreground">
          These items form the basis of your product knowledge quiz. Edit existing entries with your specific products, and add new items your team needs to know. Tap a card to reveal the answer.
        </p>
      </div>

      {/* Category tabs */}
      <div className="flex flex-wrap gap-1.5">
        {allCats.map(c => (
          <button key={c} onClick={() => { setCat(c); setFlipped({}); setAdding(false); setEditIdx(null); }}
            className={cn('text-xs px-3 py-1.5 rounded-full border font-medium transition-all', c === cat ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:border-primary/40')}>
            {c} <span className="opacity-60 ml-1">{(items[c] || []).length}</span>
          </button>
        ))}
      </div>

      {/* Items as flip cards */}
      <div className="space-y-2">
        {catItems.map((item, i) => {
          const key = `${cat}-${i}`;
          const isFlipped = flipped[key];
          if (editIdx === i) {
            return (
              <EditCard key={key} item={item} onSave={(q, a) => updateItem(i, q, a)} onCancel={() => setEditIdx(null)} />
            );
          }
          return (
            <div key={key} className={cn('border rounded-xl cursor-pointer transition-all select-none overflow-hidden', isFlipped ? 'border-primary/40 bg-primary/5' : 'border-border bg-card hover:border-primary/30')}
              onClick={() => setFlipped(f => ({ ...f, [key]: !f[key] }))}>
              <div className="px-4 py-3 flex items-start gap-3">
                <span className="text-base shrink-0">{isFlipped ? '📖' : '❓'}</span>
                <div className="flex-1 min-w-0">
                  <p className={cn('text-sm font-medium leading-snug', isFlipped ? 'text-muted-foreground text-xs' : 'text-foreground')}>{item.q}</p>
                  {isFlipped && <p className="text-sm text-foreground font-semibold mt-1.5 leading-relaxed">{item.a}</p>}
                </div>
                <div className="flex items-center gap-1 shrink-0" onClick={e => e.stopPropagation()}>
                  <button className="text-muted-foreground hover:text-primary p-1 rounded" onClick={() => setEditIdx(i)}>✏️</button>
                  <button className="text-muted-foreground hover:text-destructive p-1 rounded" onClick={() => deleteItem(i)}><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
              {!isFlipped && <div className="px-4 pb-2"><p className="text-xs text-muted-foreground italic">Tap to reveal answer</p></div>}
            </div>
          );
        })}
      </div>

      {/* Add new item */}
      {adding ? (
        <div className="border border-primary/30 rounded-xl bg-primary/5 p-4 space-y-3">
          <p className="text-xs font-semibold text-foreground">New item for: {cat}</p>
          <Input placeholder="Question — e.g. What is our house gin?" value={newQ} onChange={e => setNewQ(e.target.value)} className="text-sm" />
          <Input placeholder="Answer — e.g. Tanqueray, served in a Copa glass…" value={newA} onChange={e => setNewA(e.target.value)} className="text-sm" />
          <div className="flex gap-2">
            <Button size="sm" onClick={addItem} className="gap-1.5"><Plus className="w-3.5 h-3.5" /> Add</Button>
            <Button size="sm" variant="outline" onClick={() => { setAdding(false); setNewQ(''); setNewA(''); }}>Cancel</Button>
          </div>
        </div>
      ) : (
        <Button variant="outline" size="sm" className="w-full gap-1.5 border-dashed" onClick={() => setAdding(true)}>
          <Plus className="w-3.5 h-3.5" /> Add item to {cat}
        </Button>
      )}
    </div>
  );
}

function EditCard({ item, onSave, onCancel }: { item: { q: string; a: string }; onSave: (q: string, a: string) => void; onCancel: () => void }) {
  const [q, setQ] = useState(item.q);
  const [a, setA] = useState(item.a);
  return (
    <div className="border border-primary/40 rounded-xl bg-primary/5 p-4 space-y-2">
      <Input value={q} onChange={e => setQ(e.target.value)} className="text-sm font-medium" />
      <Input value={a} onChange={e => setA(e.target.value)} className="text-sm" />
      <div className="flex gap-2 pt-1">
        <Button size="sm" onClick={() => onSave(q, a)} className="gap-1.5">✓ Save</Button>
        <Button size="sm" variant="outline" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────

export default function EpicPubGuide() {
  return (
    <div className="mt-6 border border-primary/20 rounded-2xl overflow-hidden">
      <div className="bg-gradient-to-r from-primary/10 to-primary/5 border-b border-primary/20 px-5 py-4">
        <h3 className="font-bold text-foreground text-base flex items-center gap-2">
          🍸 Epic Pub — House Style & Product Knowledge
        </h3>
        <p className="text-sm text-muted-foreground mt-0.5">
          Interactive pouring guide, wine measures, and your product knowledge flashcards.
        </p>
      </div>
      <div className="p-4">
        <Tabs defaultValue="pour">
          <TabsList className="w-full mb-5">
            <TabsTrigger value="pour" className="flex-1 text-xs sm:text-sm">🫗 Pouring Guide</TabsTrigger>
            <TabsTrigger value="wine" className="flex-1 text-xs sm:text-sm">🍷 Wine Measures</TabsTrigger>
            <TabsTrigger value="knowledge" className="flex-1 text-xs sm:text-sm">📚 Product Knowledge</TabsTrigger>
          </TabsList>
          <TabsContent value="pour"><PourGuideSection /></TabsContent>
          <TabsContent value="wine"><WineGuideSection /></TabsContent>
          <TabsContent value="knowledge"><ProductKnowledgeSection /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
